/**
 * AddButton Component
 * Specialized button for adding items with consistent styling
 */

import React from "react";
import { Button as ChakraButton, ButtonProps as ChakraButtonProps } from "@chakra-ui/react";
import { resolveSpacing } from "design-system/foundation";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import CDS from "design-system";

// Branded types for AddButton variants
declare const __add_button_variant_brand: unique symbol;
declare const __add_button_size_brand: unique symbol;

export interface AddButtonVariantToken {
  readonly [__add_button_variant_brand]: true;
  readonly _value: string;
}

export interface AddButtonSizeToken {
  readonly [__add_button_size_brand]: true;
  readonly _value: string;
}

// Factory functions
const createAddButtonVariantToken = (value: string): AddButtonVariantToken => ({ _value: value } as AddButtonVariantToken);

const createAddButtonSizeToken = (value: string): AddButtonSizeToken => ({ _value: value } as AddButtonSizeToken);

export interface AddButtonProps extends Omit<ChakraButtonProps, "variant" | "size"> {
  children?: React.ReactNode;
  variant?: AddButtonVariantToken;
  size?: AddButtonSizeToken;
  hideIcon?: boolean;
}

function AddButtonComponent({
  children = "Add",
  variant = addButtonTokens.variant.primary,
  size = addButtonTokens.size.medium,
  hideIcon = false,
  ...props
}: AddButtonProps) {
  // Use color mode reactive colors
  const primaryBg = useColorModeValue(CDS.Color.interactive.primary);
  const primaryHoverBg = useColorModeValue(CDS.Color.interactive.primaryHover);
  const primaryActiveBg = useColorModeValue(CDS.Color.interactive.primaryActive);
  const textInverse = useColorModeValue(CDS.Color.text.inverse);
  const textPrimary = useColorModeValue(CDS.Color.text.primary);
  const surfaceSecondary = useColorModeValue(CDS.Color.surface.secondary);
  const surfaceTertiary = useColorModeValue(CDS.Color.surface.tertiary);
  const borderPrimary = useColorModeValue(CDS.Color.border.primary);

  const getVariantStyles = (variant: AddButtonVariantToken) => {
    switch (variant._value) {
      case "primary":
        return {
          bg: primaryBg,
          color: textInverse,
          _hover: {
            bg: primaryHoverBg,
          },
          _active: {
            bg: primaryActiveBg,
          },
        };
      case "secondary":
        return {
          bg: surfaceSecondary,
          color: textPrimary,
          border: "1px solid",
          borderColor: borderPrimary,
          _hover: {
            bg: surfaceTertiary,
          },
        };
      case "ghost":
        return {
          bg: "transparent",
          color: primaryBg,
          _hover: {
            bg: surfaceSecondary,
          },
        };
      default:
        return {};
    }
  };

  const getSizeStyles = (size: AddButtonSizeToken) => {
    switch (size._value) {
      case "small":
        return {
          h: resolveSpacing(CDS.Spacing.extraLarge), // 32px
          px: resolveSpacing(CDS.Spacing.small),
          fontSize: "sm",
        };
      case "medium":
        return {
          h: "40px",
          px: resolveSpacing(CDS.Spacing.medium),
        };
      case "large":
        return {
          h: resolveSpacing(CDS.Spacing.huge), // 48px
          px: resolveSpacing(CDS.Spacing.large),
          fontSize: "lg",
        };
      default:
        return {};
    }
  };

  return (
    <ChakraButton
      leftIcon={!hideIcon ? <CDS.Icon type={CDS.Icon.type.plus} color={CDS.Color.text.inverse} /> : undefined}
      {...getVariantStyles(variant)}
      {...getSizeStyles(size)}
      {...props}
    >
      {children}
    </ChakraButton>
  );
}

// Button tokens
const addButtonTokens = {
  variant: {
    primary: createAddButtonVariantToken("primary"),
    secondary: createAddButtonVariantToken("secondary"),
    ghost: createAddButtonVariantToken("ghost"),
  } as const,

  size: {
    small: createAddButtonSizeToken("small"),
    medium: createAddButtonSizeToken("medium"),
    large: createAddButtonSizeToken("large"),
  } as const,
} as const;

// Public API - combines tokens and component
export const AddButton = Object.assign(AddButtonComponent, addButtonTokens);
